import { ACCENT } from '../catalog/accent';
import type { AccentKey } from '../catalog/accent';
import { CHANNEL_KIND } from '../catalog/channel';
import { RAIL_TYPE_KEYS, RAIL_TYPE_SPECS } from '../catalog/railType';
import type { RailTypeKey } from '../catalog/railType';
import type { RcuConfig, WiredLoad } from '../model';
import { wiredLoads } from './grouping';
import { channelKey, railsOfType } from './lookup';

/**
 * One band of the capacity meter: how many outputs a hardware family provides
 * and how many of them already drive a load.
 */
export interface CapacityBand {
  readonly id: string;
  readonly label: string;
  readonly accent: AccentKey;
  readonly total: number;
  readonly used: number;
}

const RCU_BAND_ID = CHANNEL_KIND.RCU;

/**
 * Modules fitted to the RCU: DIN rails plus DALI bridges. The single number the
 * module budget is checked against, so rails and bridges cannot each believe
 * they have the whole allowance.
 */
export const moduleCount = (config: RcuConfig): number =>
  config.rails.length + config.daliBridges.length;

/** Outputs that currently drive a load. Counted by channel key, so a duplicate wiring counts once. */
export const wiredOutputCount = (config: RcuConfig): number =>
  new Set(wiredLoads(config).map((load) => channelKey(load.channel))).size;

export const totalOutputCount = (config: RcuConfig): number =>
  config.rcu.outputCount +
  RAIL_TYPE_KEYS.reduce(
    (sum, type) => sum + railsOfType(config, type).length * RAIL_TYPE_SPECS[type].channels,
    0,
  );

export const freeOutputCount = (config: RcuConfig): number =>
  totalOutputCount(config) - wiredOutputCount(config);

const loadsOnRails = (config: RcuConfig, railIds: ReadonlySet<string>): readonly WiredLoad[] =>
  wiredLoads(config).filter(
    (load) => load.channel.kind === CHANNEL_KIND.DIN && railIds.has(load.channel.railId),
  );

const railBand = (config: RcuConfig, type: RailTypeKey): CapacityBand => {
  const rails = railsOfType(config, type);
  const railIds = new Set<string>(rails.map((rail) => rail.id));
  return {
    id: type,
    label: RAIL_TYPE_SPECS[type].shortLabel,
    accent: RAIL_TYPE_SPECS[type].accent,
    total: rails.length * RAIL_TYPE_SPECS[type].channels,
    used: loadsOnRails(config, railIds).length,
  };
};

/** Meter bands: onboard outputs first, then each rail type that is fitted. */
export const capacityBands = (config: RcuConfig): readonly CapacityBand[] => {
  const rcuBand: CapacityBand = {
    id: RCU_BAND_ID,
    label: 'RCU outputs',
    accent: ACCENT.SLATE,
    total: config.rcu.outputCount,
    used: wiredLoads(config).filter((load) => load.channel.kind === CHANNEL_KIND.RCU).length,
  };
  const fittedRailBands = RAIL_TYPE_KEYS.filter((type) => railsOfType(config, type).length > 0).map(
    (type) => railBand(config, type),
  );
  return [rcuBand, ...fittedRailBands];
};
